/**
 * NotificationPreferences Component
 *
 * Allows users to configure their vacation notification preferences.
 * Supports individual notification type toggles and digest frequency selection.
 *
 * Features:
 * - Enable/disable individual notification types
 * - Configure notification frequency (immediate, daily, weekly)
 * - Digest mode selection for batched notifications
 * - Real-time validation and save feedback
 * - Radix UI primitives with Tailwind styling
 */

import React, { useState, useEffect } from 'react'
import { Bell, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

export interface PreferenceItem {
  enabled: boolean
  frequency: 'immediate' | 'daily' | 'weekly'
}

export interface NotificationPreferences {
  requestSubmitted?: PreferenceItem
  approvalReceived?: PreferenceItem
  rejectionReceived?: PreferenceItem
  expiringBalance?: PreferenceItem
  digestFrequency?: 'immediate' | 'daily' | 'weekly'
}

export interface NotificationPreferencesProps {
  onSave?: (preferences: NotificationPreferences) => Promise<void>
  onLoad?: () => Promise<NotificationPreferences | null>
  className?: string
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  requestSubmitted: { enabled: true, frequency: 'immediate' },
  approvalReceived: { enabled: true, frequency: 'immediate' },
  rejectionReceived: { enabled: true, frequency: 'immediate' },
  expiringBalance: { enabled: true, frequency: 'immediate' },
  digestFrequency: 'immediate'
}

const NOTIFICATION_LABELS = {
  requestSubmitted: 'Confirmação de Solicitação Enviada',
  approvalReceived: 'Férias Aprovadas',
  rejectionReceived: 'Férias Negadas',
  expiringBalance: 'Saldo Expirando em Breve'
}

const NOTIFICATION_DESCRIPTIONS = {
  requestSubmitted:
    'Receba um email de confirmação quando você enviar uma solicitação de férias',
  approvalReceived: 'Seja notificado quando seu gerente aprovar suas férias',
  rejectionReceived: 'Receba feedback quando suas férias forem negadas',
  expiringBalance: 'Alertas sobre dias de férias que estão próximos do vencimento (60, 30, 14 dias)'
}

/**
 * NotificationPreferences Component
 *
 * Renders notification preference configuration form
 */
export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({
  onSave,
  onLoad,
  className
}) => {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!onLoad) return

      setLoading(true)
      try {
        const loadedPreferences = await onLoad()
        if (loadedPreferences) {
          setPreferences(loadedPreferences)
        }
      } catch (error: any) {
        console.error('Failed to load notification preferences:', error)
        setErrorMessage('Erro ao carregar preferências de notificação')
      } finally {
        setLoading(false)
      }
    }

    loadPreferences()
  }, [onLoad])

  // Update individual notification preference
  const handleNotificationToggle = (type: keyof NotificationPreferences, enabled: boolean) => {
    setPreferences((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] as PreferenceItem),
        enabled
      }
    }))
    setSaveStatus(null)
  }

  // Update digest frequency
  const handleDigestFrequencyChange = (frequency: 'immediate' | 'daily' | 'weekly') => {
    setPreferences((prev) => ({
      ...prev,
      digestFrequency: frequency
    }))
    setSaveStatus(null)
  }

  // Validate preferences before saving
  const validatePreferences = (): boolean => {
    // If digest mode is enabled, at least one notification type must be enabled
    if (preferences.digestFrequency && preferences.digestFrequency !== 'immediate') {
      const hasEnabledNotification = Object.keys(preferences).some((key) => {
        if (key === 'digestFrequency') return false
        const pref = preferences[key as keyof NotificationPreferences] as PreferenceItem
        return pref?.enabled === true
      })

      if (!hasEnabledNotification) {
        setErrorMessage(
          'Pelo menos um tipo de notificação deve estar habilitado ao usar modo resumo'
        )
        return false
      }
    }

    return true
  }

  // Save preferences
  const handleSave = async () => {
    if (!onSave) return

    if (!validatePreferences()) {
      setSaveStatus('error')
      return
    }

    setSaving(true)
    setSaveStatus(null)
    setErrorMessage(null)

    try {
      await onSave(preferences)
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error: any) {
      console.error('Failed to save notification preferences:', error)
      setErrorMessage(error.message || 'Erro ao salvar preferências')
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <article className={cn('rounded-lg border bg-card', className)}>
      {/* Header */}
      <header className="flex items-center gap-3 border-b p-6">
        <Bell className="h-5 w-5 text-primary" aria-hidden="true" />
        <div>
          <h2 className="text-lg font-semibold">Preferências de Notificação</h2>
          <p className="text-sm text-muted-foreground">
            Configure como você deseja receber notificações sobre suas férias
          </p>
        </div>
      </header>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Notification Type Toggles */}
        <div>
          <h3 className="text-sm font-medium mb-4">Tipos de Notificação</h3>
          <div className="space-y-4">
            {Object.entries(NOTIFICATION_LABELS).map(([key, label]) => {
              const prefKey = key as keyof typeof NOTIFICATION_LABELS
              const pref = preferences[prefKey] as PreferenceItem | undefined
              const switchId = `notification-${key}`

              return (
                <div key={key} className="flex items-start justify-between gap-4">
                  <label htmlFor={switchId} className="flex flex-col gap-1 flex-1 cursor-pointer">
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground">
                      {NOTIFICATION_DESCRIPTIONS[prefKey]}
                    </span>
                  </label>
                  <Switch
                    id={switchId}
                    checked={pref?.enabled ?? false}
                    onCheckedChange={(checked) => handleNotificationToggle(prefKey, checked)}
                    aria-label={`Toggle ${label}`}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Divider */}
        <hr className="border-t border-border" />

        {/* Digest Frequency Selection */}
        <div>
          <fieldset>
            <legend className="text-sm font-medium mb-2">Frequência de Envio</legend>
            <p className="text-xs text-muted-foreground mb-4">
              Escolha quando você deseja receber as notificações
            </p>
            <RadioGroup
              value={preferences.digestFrequency || 'immediate'}
              onValueChange={(value) =>
                handleDigestFrequencyChange(value as 'immediate' | 'daily' | 'weekly')
              }
            >
              <div className="flex items-start gap-3">
                <RadioGroupItem value="immediate" id="immediate" aria-label="Imediato" />
                <label htmlFor="immediate" className="flex flex-col gap-1 flex-1 cursor-pointer">
                  <span className="text-sm font-medium">Imediato</span>
                  <span className="text-xs text-muted-foreground">
                    Receba notificações assim que os eventos ocorrerem
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="daily" id="daily" aria-label="Resumo Diário" />
                <label htmlFor="daily" className="flex flex-col gap-1 flex-1 cursor-pointer">
                  <span className="text-sm font-medium">Resumo Diário</span>
                  <span className="text-xs text-muted-foreground">
                    Receba um email diário com todas as notificações do dia (18h UTC)
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="weekly" id="weekly" aria-label="Resumo Semanal" />
                <label htmlFor="weekly" className="flex flex-col gap-1 flex-1 cursor-pointer">
                  <span className="text-sm font-medium">Resumo Semanal</span>
                  <span className="text-xs text-muted-foreground">
                    Receba um email semanal com todas as notificações (segunda-feira 10h UTC)
                  </span>
                </label>
              </div>
            </RadioGroup>
          </fieldset>
        </div>

        {/* Status Messages */}
        {saveStatus === 'success' && (
          <Alert variant="success" icon={<CheckCircle className="h-4 w-4" />}>
            Preferências salvas com sucesso!
          </Alert>
        )}

        {saveStatus === 'error' && (
          <Alert variant="destructive" icon={<AlertCircle className="h-4 w-4" />}>
            {errorMessage || 'Erro ao salvar preferências. Tente novamente.'}
          </Alert>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Salvando...' : 'Salvar Preferências'}
          </Button>
        </div>
      </div>
    </article>
  )
}

export default NotificationPreferences
